import type { ObjectId } from "@bbj-types/utils";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ISocialOfferWithFavoriteChallenges } from "@root/api/services/offers/socialOffer/types";
import { Dialog, LoadingBackdrop } from "@styleguide";
import { ModalSocialOfferForm } from "./form";
import { useModalSocialOffer } from "./logics";

export interface IModalSocialOfferProps {
  open: boolean;
  socialOffer: ISocialOfferWithFavoriteChallenges | null;
  onClose: () => void;
  onUpdate?: (socialOffer: ISocialOfferWithFavoriteChallenges) => void;
  isDuplication?: boolean;
  defaultProvider?: {
    name?: string;
    providerId?: ObjectId;
    website?: string;
  };
}

export const ModalSocialOffer = ({
  open,
  socialOffer,
  onClose,
  onUpdate,
  isDuplication,
  defaultProvider,
}: IModalSocialOfferProps) => {
  const { hookForm, formRef, loading, documentsProgress, loadingFindDocuments, handleSubmit } = useModalSocialOffer(
    open,
    socialOffer,
    onClose,
    onUpdate,
    isDuplication,
    defaultProvider
  );

  const onSubmit = async (e: React.FormEvent) => {
    await handleSubmit(e);
    e.preventDefault();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason: string) => {
        if (reason !== "backdropClick") {
          loadingFindDocuments || loading ? undefined : onClose;
        }
      }}
      disableEnforceFocus
    >
      <DialogTitle component={Stack} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">
          {isDuplication
            ? "Dupliquer une offre de service"
            : socialOffer
              ? "Modifier une offre de service"
              : "Nouvelle offre de service"}
        </Typography>
        <IconButton onClick={onClose} sx={{ mr: "-10px" }} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {!socialOffer && (
          <Typography variant="body1">
            <Typography variant="body1SemiBold">
              Vous avez trouvé une offre de service intéressante en dehors d'Horizon ?
            </Typography>
            <br />
            Ajoutez-la sur Horizon en renseignant les informations ci-dessous.
          </Typography>
        )}
        <ModalSocialOfferForm
          hookForm={hookForm}
          onSubmit={onSubmit}
          formRef={formRef}
          loading={loading}
          documentsProgress={documentsProgress}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Tooltip
          title={
            Object.keys(hookForm.formState.dirtyFields).length <= 0 && isDuplication
              ? "Les données sont exactement identiques à celles de l'offre que vous souhaitez dupliquer. Veuillez modifier au moins un champ pour dupliquer l'offre et ainsi en créer une nouvelle."
              : ""
          }
        >
          <span>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={loading}
              onClick={() => {
                if (formRef.current) {
                  formRef.current.requestSubmit();
                }
              }}
            >
              {isDuplication ? "Dupliquer" : socialOffer ? "Enregistrer" : "Créer"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
      <LoadingBackdrop loading={loadingFindDocuments} />
    </Dialog>
  );
};
